// src/pages/Usuarios.jsx - Gestión de Usuarios con Estilos Alianza Social
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Box,
  Alert,
  CircularProgress,
  Avatar,
  Chip,
  Button,
  TablePagination
} from '@mui/material';

import {
  Group as GroupIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Add as AddIcon,
  Email as EmailIcon,
  AccountCircle as AccountCircleIcon
} from '@mui/icons-material';

import { getUsuarios } from '../../actions/UsuarioAction';
import Swal from 'sweetalert2';

const Usuarios = () => {
  const navigate = useNavigate();
  
  const [requestUsuarios, setRequestUsuarios] = useState({
    pageIndex: 1,
    pageSize: 10,
    search: ''
  });

  const [paginadorUsuarios, setPaginadorUsuarios] = useState({
    count: 0,
    pageIndex: 0,
    pageSize: 0,
    pageCount: 0,
    data: []
  });

  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    getListaUsuarios();
  }, [requestUsuarios]);

  const getListaUsuarios = async () => {
    setLoading(true);
    
    try {
      const response = await getUsuarios(requestUsuarios);
      
      if (response.status === 200) {
        setPaginadorUsuarios(response.data);
        setSuccessMessage('Usuarios cargados correctamente');
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        setErrorMessage('Problemas al cargar usuarios');
        Swal.fire({
          title: 'Errores cargando usuarios',
          text: 'Problemas de solicitud.',
          icon: 'warning'
        });
      }
    } catch (error) {
      setErrorMessage('Error de conexión al cargar usuarios');
      Swal.fire({
        title: 'Errores cargando usuarios',
        text: 'Problemas de conexión.',
        icon: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setRequestUsuarios(prev => ({
      ...prev,
      pageIndex: newPage + 1
    }));
  };

  const handleChangeRowsPerPage = (event) => {
    setRequestUsuarios(prev => ({
      ...prev,
      pageSize: parseInt(event.target.value, 10),
      pageIndex: 1
    }));
  };

  const editarUsuario = (id) => {
    navigate(`/admin/usuario/${id}`);
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await getListaUsuarios();
    setRefreshing(false);
  };

  const handleEliminarUsuario = (usuario) => {
    Swal.fire({
      title: '¿Está seguro de eliminar este usuario?',
      html: `${usuario.nombre} ${usuario.apellido}<br>No podrás revertir esto!`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sí, eliminar!',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#E63946',
      cancelButtonColor: '#757575'
    }).then((result) => {
      if (result.isConfirmed) {
        // Aquí iría la lógica de eliminación
        Swal.fire(
          'Eliminado!',
          'El usuario ha sido eliminado.',
          'success'
        );
      }
    });
  };

  // Estadísticas
  const stats = {
    total: paginadorUsuarios.count || 0,
    activos: paginadorUsuarios.data.filter(u => !u.lockoutEnabled).length,
    bloqueados: paginadorUsuarios.data.filter(u => u.lockoutEnabled).length
  };

  return (
    <Container 
      maxWidth="xl" 
      sx={{ 
        mt: 7, 
        mb: 4,
        backgroundColor: 'rgba(255, 255, 255, 0.35)',
        backdropFilter: 'blur(10px)',
        borderRadius: '24px',
        padding: '32px',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.38)'
      }}
    >
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Box>
            <Typography variant="h4" fontWeight="bold" color="#1E88E5" gutterBottom>
              <GroupIcon sx={{ fontSize: 40, mr: 1, verticalAlign: 'middle' }} />
              Gestión de Usuarios
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Lista general de usuarios en el sistema
            </Typography>
          </Box>
          
          <Box display="flex" gap={2}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={handleRefresh}
              disabled={refreshing}
              sx={{
                borderRadius: '12px',
                borderWidth: '2px',
                borderColor: '#1E88E5',
                color: '#1E88E5',
                '&:hover': {
                  borderWidth: '2px',
                  borderColor: '#1565C0',
                  backgroundColor: 'rgba(30, 136, 229, 0.04)'
                }
              }}
            >
              Actualizar
            </Button>
            
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate('/admin/usuario/nuevo')}
              sx={{
                borderRadius: '12px',
                backgroundColor: '#1E88E5',
                '&:hover': {
                  backgroundColor: '#1565C0'
                }
              }}
            >
              Nuevo Usuario
            </Button>
          </Box>
        </Box>

        {/* Mensajes */}
        {successMessage && (
          <Alert 
            severity="success" 
            sx={{ mb: 2, borderRadius: '12px' }} 
            onClose={() => setSuccessMessage('')}
          >
            {successMessage}
          </Alert>
        )}
        {errorMessage && (
          <Alert 
            severity="error" 
            sx={{ mb: 2, borderRadius: '12px' }} 
            onClose={() => setErrorMessage('')}
          >
            {errorMessage}
          </Alert>
        )}
      </Box>

      {/* Estadísticas */}
      <Grid container spacing={3} sx={{ mb: 4 }} justifyContent="center">
        <Grid item xs={12} sm={4}>
          <Card sx={{ borderRadius: '16px', border: '2px solid #1E88E5' }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="h4" fontWeight="bold" color="#1E88E5">
                    {stats.total}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Usuarios
                  </Typography>
                </Box>
                <GroupIcon sx={{ fontSize: 50, color: '#1E88E5', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={4}>
          <Card sx={{ borderRadius: '16px', border: '2px solid #4CAF50' }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="h4" fontWeight="bold" color="#4CAF50">
                    {stats.activos}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Activos
                  </Typography>
                </Box>
                <AccountCircleIcon sx={{ fontSize: 50, color: '#4CAF50', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={4}>
          <Card sx={{ borderRadius: '16px', border: '2px solid #E63946' }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="h4" fontWeight="bold" color="#E63946">
                    {stats.bloqueados}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Bloqueados
                  </Typography>
                </Box>
                <AccountCircleIcon sx={{ fontSize: 50, color: '#E63946', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabla */}
      <Card sx={{ borderRadius: '16px', overflow: 'hidden' }}>
        <CardContent sx={{ p: 0 }}>
          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
              <CircularProgress size={60} sx={{ color: '#1E88E5' }} />
            </Box>
          ) : (
            <>
              <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ backgroundColor: '#F5F5F5' }}>
                        <Typography variant="subtitle2" fontWeight="bold">
                          Usuario
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ backgroundColor: '#F5F5F5' }}>
                        <Typography variant="subtitle2" fontWeight="bold">
                          Email
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ backgroundColor: '#F5F5F5' }}>
                        <Typography variant="subtitle2" fontWeight="bold">
                          Username
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ backgroundColor: '#F5F5F5' }}>
                        <Typography variant="subtitle2" fontWeight="bold">
                          Estado
                        </Typography>
                      </TableCell>
                      <TableCell align="center" sx={{ backgroundColor: '#F5F5F5' }}>
                        <Typography variant="subtitle2" fontWeight="bold">
                          Acciones
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginadorUsuarios.data.map((usuario) => (
                      <TableRow key={usuario.id} hover>
                        <TableCell>
                          <Box display="flex" alignItems="center">
                            <Avatar 
                              sx={{ 
                                bgcolor: '#1E88E5', 
                                width: 40, 
                                height: 40,
                                mr: 2
                              }}
                              src={usuario.imagen}
                            >
                              {usuario.nombre?.charAt(0)}{usuario.apellido?.charAt(0)}
                            </Avatar>
                            <Box>
                              <Typography variant="body2" fontWeight="medium">
                                {usuario.nombre} {usuario.apellido}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                ID: {usuario.id.substring(0, 8)}...
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        
                        <TableCell>
                          <Box display="flex" alignItems="center">
                            <EmailIcon fontSize="small" sx={{ mr: 1, color: '#757575' }} />
                            <Typography variant="body2">
                              {usuario.email}
                            </Typography>
                          </Box>
                        </TableCell>
                        
                        <TableCell>
                          <Typography variant="body2">
                            {usuario.userName}
                          </Typography>
                        </TableCell>

                        <TableCell>
                          <Chip
                            label={usuario.lockoutEnabled ? 'Bloqueado' : 'Activo'}
                            size="small"
                            sx={{
                              backgroundColor: usuario.lockoutEnabled ? '#FFEBEE' : '#E8F5E9',
                              color: usuario.lockoutEnabled ? '#C62828' : '#2E7D32',
                              fontWeight: 600,
                              fontSize: '0.75rem'
                            }}
                          />
                        </TableCell>
                        
                        <TableCell align="center">
                          <Tooltip title="Editar">
                            <IconButton
                              size="small"
                              onClick={() => editarUsuario(usuario.id)}
                              sx={{ 
                                color: '#1E88E5',
                                '&:hover': { backgroundColor: 'rgba(30, 136, 229, 0.1)' }
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Eliminar">
                            <IconButton
                              size="small"
                              onClick={() => handleEliminarUsuario(usuario)}
                              sx={{ 
                                color: '#E63946',
                                '&:hover': { backgroundColor: 'rgba(230, 57, 70, 0.1)' }
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Paginación */}
              <TablePagination
                component="div"
                count={paginadorUsuarios.count}
                page={paginadorUsuarios.pageIndex - 1}
                onPageChange={handleChangePage}
                rowsPerPage={paginadorUsuarios.pageSize}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 25, 50]}
                labelRowsPerPage="Filas por página:"
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
              />
            </>
          )}
        </CardContent>
      </Card>
    </Container>
  );
};

export default Usuarios;